import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { ShrinkContent } from '../ShrinkContent';
import ItemsSummaryList from './ItemsSummaryList';
import {
  asCurrency,
  asQuantity,
  asQuantityWithUnit,
} from '../../utils/formatters';
import { useFilterContext } from '../../contexts/FilterContext';
import { sum } from '../../utils/aggregators';

const options = [
  {
    key: 'geneticStock',
    value: 'geneticStock',
    name: 'Genetic Stock',
  },
  {
    key: 'equipment',
    value: 'equipment',
    name: 'Equipment & Infra',
  },
  {
    key: 'inputs',
    value: 'inputs',
    name: 'Materials & Fuel',
  },
  {
    key: 'labor',
    value: 'labor',
    name: 'Labor',
  },
];

// Get the year with the maximum value for the given accessor
const maxValueYear = (items, valueAccessor, years) => {
  // Sum the item values by year
  const totalsByYear = Array.from({ length: years }).map((c, i) =>
    _.sumBy(items, (entry) => entry[valueAccessor][i]),
  );
  // Find the year with the maximum total cost
  return totalsByYear.indexOf(_.max(totalsByYear));
};

const getMetrics = (prorated) => ({
  geneticStock: {
    accessor: 'geneticStockCost',
    aggregationInfo: 'Total',
    format: asCurrency,
    viewMoreLink: '../capex',
    getItems: (cropAggregates, accessor, years) =>
      cropAggregates.map(
        ({ crop, economicSummary: { geneticsCostByYear } }) => ({
          ...crop,
          [accessor]: sum(geneticsCostByYear.slice(0, years)),
        }),
      ),
  },
  equipment: {
    accessor: 'capexCost',
    format: asCurrency,
    aggregationInfo: prorated ? 'Prorated Cost' : 'Total',
    viewMoreLink: '../capex',
    getItems: (cropAggregates, accessor, years) =>
      _.uniqBy(
        cropAggregates.flatMap(({ proratedCapexes }) => proratedCapexes),
        'capex.id',
      ).map(({ capex, proratedCostByYear }) => ({
        ...capex,
        [accessor]: sum(
          (prorated ? proratedCostByYear : capex.totalCostByYear).slice(
            0,
            years,
          ),
        ),
      })),
  },
  inputs: {
    accessor: 'maxYearCost',
    format: asCurrency,
    aggregationInfo: 'Maximum Annual Expenses',
    viewMoreLink: '../inputs',
    getItems: (cropAggregates, accessor, years) => {
      // Input aggregates across all selected crops
      const items = cropAggregates.flatMap(
        ({ inputAggregates }) => inputAggregates,
      );

      const maxYear = maxValueYear(items, 'totalCostByYear', years);

      // Get the items and their associated values for the maximum value year
      const itemsWithMaxYearCost = items.map(({ input, totalCostByYear }) => ({
        ...input,
        [accessor]: totalCostByYear[maxYear],
      }));

      // More than one crop may use the same input,
      // so group the items by id and sum the costs.
      return Object.values(_.groupBy(itemsWithMaxYearCost, 'id')).map(
        (itemGroup) => ({
          ...itemGroup[0],
          [accessor]: _.sumBy(itemGroup, accessor),
        }),
      );
    },
  },
  labor: {
    accessor: 'maxYearHours',
    format: asQuantityWithUnit('hours', 'hour'),
    csvFormat: asQuantity,
    unit: 'Hours',
    viewMoreLink: '../labor',
    getItems: (cropAggregates, accessor, years) => {
      const items = cropAggregates.flatMap(
        ({ operationAggregates }) => operationAggregates,
      );
      const maxYear = maxValueYear(items, 'hoursOfLaborByYear', years);
      const itemsWithMaxYearHours = items.map(
        ({ operation, hoursOfLaborByYear }) => ({
          ...operation,
          [accessor]: hoursOfLaborByYear[maxYear],
        }),
      );
      // More than one crop may have an operation with the same name,
      // so group the items by name and sum the hours.
      return Object.values(_.groupBy(itemsWithMaxYearHours, 'name')).map(
        (itemGroup) => ({
          ...itemGroup[0],
          [accessor]: _.sumBy(itemGroup, accessor),
        }),
      );
    },
    aggregationInfo: 'Maximum Annual Labor',
  },
});

const ExpensesSummary = ({ cropAggregates = [], className }) => {
  const { years, crop } = useFilterContext().state;
  const [chartOption, setChartOption] = useState(options[0]);

  const valueColumn = getMetrics(!!crop)[chartOption.key];
  const { accessor, aggregationInfo, getItems, viewMoreLink } = valueColumn;

  const activeItems = useMemo(
    () =>
      _.orderBy(getItems(cropAggregates, accessor, years), accessor, 'desc'),
    [accessor, cropAggregates, getItems, years],
  );

  const dropdownProps = {
    options,
    selectedOption: chartOption.name,
    setOption: setChartOption,
  };

  return (
    <ShrinkContent className={className}>
      <ItemsSummaryList
        items={activeItems}
        valueColumn={valueColumn}
        dropdownProps={dropdownProps}
        aggregationInfo={aggregationInfo}
        viewMoreLink={viewMoreLink}
      />
    </ShrinkContent>
  );
};

export default ExpensesSummary;
